import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setToast } from "../../Utils/Toast";
import axios from "axios";
import { LOGOUT } from "../../Routes/AppRoutes";
import { Container, Form, Card } from "react-bootstrap";
import { useOrganization } from "../../Utils/OrganizationContext";
import useAxiosInstance from "../../Utils/Headers";
import { getCookie } from "typescript-cookie";
import { X } from "react-bootstrap-icons";

function UploadProductCSV() {
  const { selectedOrganizationId }: any = useOrganization();
  const organization_id = getCookie("organizationId");
  const [organizationID, setOrganizationID] = useState<any>();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fileValue, setFileValue] = useState<File | null>(null);
  const [failedImages, setFailedImages] = useState<any[]>([]);
  const axiosInstance = useAxiosInstance();
  const fileInputRef = useRef<any>(null);

  useEffect(() => {
    if (selectedOrganizationId) {
      setOrganizationID(selectedOrganizationId);
    } else {
      setOrganizationID(organization_id);
    }
    // eslint-disable-next-line
  }, [selectedOrganizationId]);

  const handleSubmit = async (event: any) => {
    try {
      event.preventDefault();
      if (!fileValue) {
        setError("Please choose a CSV file to upload.");
        return;
      }
      setError("");
      setLoading(true);
      setFailedImages([]);

      const formData = new FormData();
      formData.append("csv_file", fileValue);
      formData.append("organization_id", organizationID);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}api/product-csv-import`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setToast("success", `Product CSV imported successfully.`);
      setFileValue(null);
      if (response.data.failed_images?.length > 0) {
        setFailedImages(response.data.failed_images);
      } else {
        navigate(-1);
      }
    } catch (error: any) {
      setToast("error", error.response?.data?.message || "Upload failed");

      if (axios.isAxiosError(error)) {
        if (error.response && error.response.status === 401) {
          navigate(LOGOUT);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  const handleClose = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // This clears the file input field
    }
    setFileValue(null);
    setError("");
    setFailedImages([]);
  };

  return (
    <Container>
      {failedImages.length > 0 && (
        <div
          className="alert alert-danger alert-dismissible fade show mt-4"
          role="alert"
        >
          <strong>Failed Products Import:</strong>
          <div className="mb-2">The following products failed to import:</div>
          <div className="table-responsive">
            <table className="table table-sm table-bordered text-nowrap failed-products-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product Name</th>
                  <th className="text-center">Image</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {failedImages.map((item, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item?.product_name || "N/A"}</td>
                    <td className="text-center align-middle">
                      <img
                        src={
                          item?.image?.trim()
                            ? item.image
                            : "/images/no-image.png"
                        }
                        alt={item?.product_name || "Product"}
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "contain",
                        }}
                        onError={(e: any) => {
                          e.target.onerror = null;
                          e.target.src = "/images/no-image.png";
                        }}
                      />
                    </td>
                    <td className="text-danger">
                      {item?.error || "Unknown error"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="btn-close"
            style={{
              color: "black",
              height: "20px",
              width: "20px",
              zIndex: "9999",
            }}
            data-bs-dismiss="alert"
            aria-label="Close"
            onClick={handleClose}
          >
            <X size={"xxl"} />
          </button>
        </div>
      )}

      <Card className="mt-5">
        <Card.Header>
          <h1 className="mt-4">Product CSV Import</h1>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="file" className="pt-2">
              <Form.Label>Choose CSV File*</Form.Label>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={(event: any) => {
                  const selectedFile = event.currentTarget.files[0];
                  setFileValue(selectedFile);
                }}
              />
              <p className="mb-0 fs-6 text-secondary">Upload only CSV files.</p>
              {error.length > 0 && <p className="text-danger">{error}</p>}
            </Form.Group>

            <div className="text-end">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-sm back-btn me-2 mt-4 fs-6 text-white ps-5 pe-5"
                type="button"
              >
                Back
              </button>
              <button
                type="submit"
                className="btn addnew-btn mt-4 btn-sm ms-2"
                disabled={loading}
              >
                {loading ? "Uploading..." : "Submit"}
              </button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      <Card className="csv-help mt-4">
        <Card.Body>
          <h3>Help</h3>
          <h5>Upload CSV File — Guidelines:</h5>
          <p>
            Please make sure your CSV file includes all the required fields such
            as product name, category, size, type, color, and image URLs or
            filenames. Make sure the formatting is consistent with the sample
            template provided.
          </p>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default UploadProductCSV;
